// The master calendar and the personal calendar feed (spec §33; ADR 0018).
//
// One calendar of what Hub itself knows, each item from its own module and
// shown only to someone who may see that module's records: an item you
// can't see is simply absent, never a "busy" block. Hub infers no
// availability. Booking keeps reservations, holds and load-in and load-out;
// since ADR 0023 the web Worker reads them live from Booking's room feed
// (source-rooms.ts) for holders of calendar.read and facilities.read, as
// their own "booking" items, and says so when Booking can't be read.
// Governance meetings (board, Executive Committee and committee meetings)
// are Hub's own records since ADR 0028, shown to holders of calendar.read
// and governance.read; an executive session shows only as the meeting.

import type { BookingCalendarState } from './source-rooms'

export const CALENDAR_FEED_SUB = 'system:calendar-feed'

// 'comms' (ADR 0037): the content calendar, scheduled and sent campaigns,
// journey sends due and event alerts, for holders of comms.read.
export const CALENDAR_ITEM_TYPES = ['event', 'booking', 'shift', 'work_order', 'maintenance', 'inspection', 'milestone', 'task', 'governance', 'comms'] as const
export type CalendarItemType = (typeof CALENDAR_ITEM_TYPES)[number]
export const CALENDAR_ITEM_LABELS: Record<CalendarItemType, string> = {
  event: 'Events',
  booking: 'Reservations',
  shift: 'Shifts',
  work_order: 'Work orders',
  maintenance: 'Preventive maintenance',
  inspection: 'Inspections',
  milestone: 'Project milestones',
  task: 'My tasks',
  governance: 'Governance meetings',
  comms: 'Communications',
}

// Sources Hub has no calendar read from: shown as "not connected", never as
// an empty day. (Booking has one since ADR 0023; whether it answered is in
// CalendarResponse.booking.)
// Board meetings are Hub's own governance meetings since ADR 0028, so
// nothing is listed here now.
export const CALENDAR_NOT_CONNECTED: { source: string; reason: string }[] = []

export type CalendarItem = {
  id: string
  type: CalendarItemType
  title: string
  // All-day items: YYYY-MM-DD (end inclusive). Timed items: venue-local
  // YYYY-MM-DDTHH:MM.
  start: string
  end: string | null
  allDay: boolean
  // Which part of Hub it came from ("Hub events", "Hub shifts", ...).
  source: string
  status: string | null
  // A path inside Hub.
  link: string | null
  // A page in another app (a Booking reservation), opened in a new tab.
  externalUrl?: string | null
  facilityId: string | null
  facilityName: string | null
  spaceId: string | null
  spaceName: string | null
  departmentId: string | null
  departmentName: string | null
  detail: string | null
}

export type CalendarConflict =
  | { kind: 'space'; spaceId: string; spaceName: string; itemIds: string[]; message: string }
  | { kind: 'person'; personId: string; personName: string; itemIds: string[]; message: string }

export type CalendarResponse = {
  from: string
  to: string
  items: CalendarItem[]
  conflicts: CalendarConflict[]
  // Item types this viewer may see at all.
  visibleTypes: CalendarItemType[]
  notConnected: { source: string; reason: string }[]
  // Booking's room feed (ADR 0023): whether it answered and how fresh it
  // is. Absent or null when Booking items weren't asked for.
  booking?: BookingCalendarState | null
}

export type CalendarFeedStatus = { active: { createdAt: string; lastUsedAt: string | null } | null }

// ------------------------------------------------------------------ iCal

export const escapeText = (value: string) => value.replace(/\\/g, '\\\\').replace(/\r?\n/g, '\\n').replace(/([,;])/g, '\\$1')

// Lines longer than 75 octets are folded (RFC 5545 §3.1).
export function fold(line: string): string {
  const bytes = new TextEncoder().encode(line)
  if (bytes.length <= 75) return line
  const parts: string[] = []
  let current = ''
  let size = 0
  for (const ch of line) {
    const n = new TextEncoder().encode(ch).length
    if (size + n > (parts.length === 0 ? 75 : 74)) {
      parts.push(current)
      current = ''
      size = 0
    }
    current += ch
    size += n
  }
  parts.push(current)
  return parts.join('\r\n ')
}

const local = (value: string) => value.replace(/[-:]/g, '').slice(0, 13) + '00'
const dateOnly = (day: string) => day.replace(/-/g, '')
const nextDay = (day: string) => new Date(Date.parse(`${day}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10)

const CHICAGO_TIMEZONE = [
  'BEGIN:VTIMEZONE',
  'TZID:America/Chicago',
  'BEGIN:DAYLIGHT',
  'TZOFFSETFROM:-0600',
  'TZOFFSETTO:-0500',
  'TZNAME:CDT',
  'DTSTART:19700308T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=3;BYDAY=2SU',
  'END:DAYLIGHT',
  'BEGIN:STANDARD',
  'TZOFFSETFROM:-0500',
  'TZOFFSETTO:-0600',
  'TZNAME:CST',
  'DTSTART:19701101T020000',
  'RRULE:FREQ=YEARLY;BYMONTH=11;BYDAY=1SU',
  'END:STANDARD',
  'END:VTIMEZONE',
]

// A read-only calendar of one person's own items. `stamp` is the moment the
// feed was made (UTC).
export function buildICalendar(input: { name: string; origin: string; stamp: Date; items: Pick<CalendarItem, 'id' | 'title' | 'start' | 'end' | 'allDay' | 'link' | 'facilityName' | 'spaceName' | 'detail' | 'status'>[] }): string {
  const dtstamp = input.stamp.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const lines = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//BRE Hub//Calendar feed//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', `X-WR-CALNAME:${escapeText(input.name)}`, 'X-WR-TIMEZONE:America/Chicago', ...CHICAGO_TIMEZONE]
  for (const item of input.items) {
    lines.push('BEGIN:VEVENT', `UID:${item.id}@${new URL(input.origin).host}`, `DTSTAMP:${dtstamp}`)
    if (item.allDay) {
      lines.push(`DTSTART;VALUE=DATE:${dateOnly(item.start)}`, `DTEND;VALUE=DATE:${dateOnly(nextDay(item.end ?? item.start))}`)
    } else {
      lines.push(`DTSTART;TZID=America/Chicago:${local(item.start)}`, `DTEND;TZID=America/Chicago:${local(item.end ?? item.start)}`)
    }
    lines.push(`SUMMARY:${escapeText(item.title)}`)
    const where = [item.facilityName, item.spaceName].filter(Boolean).join(', ')
    if (where) lines.push(`LOCATION:${escapeText(where)}`)
    const description = [item.detail, item.link ? `${input.origin}${item.link}` : null].filter(Boolean).join('\n')
    if (description) lines.push(`DESCRIPTION:${escapeText(description)}`)
    if (item.link) lines.push(`URL:${input.origin}${item.link}`)
    if (item.status === 'canceled' || item.status === 'cancelled') lines.push('STATUS:CANCELLED')
    lines.push('END:VEVENT')
  }
  lines.push('END:VCALENDAR')
  return lines.map(fold).join('\r\n') + '\r\n'
}

// ------------------------------------------------- meeting notice emails

// Every emailed meeting notice carries the meeting as an .ics file (ADR
// 0072): METHOD:PUBLISH, never an invitation (no Accept or Decline, nothing
// comes back by email). The UID is the calendar feed's for the same
// meeting, so a later notice updates the event already added; SEQUENCE is
// the meeting's version, which every change raises. A cancellation emailed
// by the ED is METHOD:CANCEL with STATUS:CANCELLED, the same UID and a
// higher SEQUENCE, from the same ORGANIZER.

export const meetingUid = (meetingId: string, origin: string) => `governance:${meetingId}@${new URL(origin).host}`

export type MeetingCalendarFacts = {
  meetingId: string
  title: string
  // Venue-local YYYY-MM-DDTHH:MM.
  startsLocal: string
  endsLocal: string | null
  location: string | null
  virtualLink: string | null
  // The meeting's page in myBRE, a path (/board/meetings/<id>).
  mybrePath: string
  sequence: number
}

export function buildMeetingIcs(input: {
  method: 'PUBLISH' | 'CANCEL'
  facts: MeetingCalendarFacts
  // Hub's own origin (PUBLIC_ORIGIN): the UID's host, as in the feed.
  hubOrigin: string
  // myBRE's origin, for the meeting's link; null leaves the link out.
  mybreOrigin: string | null
  organizer: { email: string; name: string }
  // CANCEL only: the person it goes to (RFC 5546 names the attendees removed).
  attendeeEmail?: string | null
  stamp: Date
}): string {
  const f = input.facts
  const dtstamp = input.stamp.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const link = input.mybreOrigin ? `${input.mybreOrigin.replace(/\/+$/, '')}${f.mybrePath}` : null
  const cancel = input.method === 'CANCEL'
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BRE Hub//Meeting notice//EN',
    'CALSCALE:GREGORIAN',
    `METHOD:${input.method}`,
    ...CHICAGO_TIMEZONE,
    'BEGIN:VEVENT',
    `UID:${meetingUid(f.meetingId, input.hubOrigin)}`,
    `SEQUENCE:${Math.max(0, Math.trunc(f.sequence))}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=America/Chicago:${local(f.startsLocal)}`,
    `DTEND;TZID=America/Chicago:${local(f.endsLocal ?? f.startsLocal)}`,
    `SUMMARY:${escapeText(f.title)}`,
    `ORGANIZER;CN=${escapeParam(input.organizer.name)}:mailto:${input.organizer.email}`,
  ]
  if (cancel && input.attendeeEmail) lines.push(`ATTENDEE:mailto:${input.attendeeEmail}`)
  const where = f.location ?? f.virtualLink
  if (where) lines.push(`LOCATION:${escapeText(where)}`)
  const description = [f.location && f.virtualLink ? f.virtualLink : null, link].filter(Boolean).join('\n')
  if (description) lines.push(`DESCRIPTION:${escapeText(description)}`)
  if (link) lines.push(`URL:${link}`)
  lines.push(`STATUS:${cancel ? 'CANCELLED' : 'CONFIRMED'}`, 'TRANSP:OPAQUE', 'END:VEVENT', 'END:VCALENDAR')
  return lines.map(fold).join('\r\n') + '\r\n'
}

// ------------------------------------------------- volunteer shift emails

// Each volunteer shift email carries one .ics per shift (ADR 0076), the
// meeting notice's rules (ADR 0072): METHOD:PUBLISH, never an invitation;
// one stable UID per assignment, `volunteer-shift:<assignment id>@<Hub
// host>`; SEQUENCE raised with every email about it; a shift canceled or a
// volunteer taken off is METHOD:CANCEL with STATUS:CANCELLED, the same UID
// and a higher SEQUENCE, the recipient as ATTENDEE.

export const volunteerShiftUid = (assignmentId: string, origin: string) => `volunteer-shift:${assignmentId}@${new URL(origin).host}`

export type ShiftCalendarFacts = {
  assignmentId: string
  // "Gate scanner, Haunt show"
  title: string
  // Venue-local YYYY-MM-DDTHH:MM.
  startsLocal: string
  endsLocal: string
  location: string | null
  // The shift in myBRE, a full URL; null leaves it out.
  link: string | null
  sequence: number
}

export function buildShiftIcs(input: { method: 'PUBLISH' | 'CANCEL'; facts: ShiftCalendarFacts; hubOrigin: string; organizer: { email: string; name: string }; attendeeEmail?: string | null; stamp: Date }): string {
  const f = input.facts
  const dtstamp = input.stamp.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
  const cancel = input.method === 'CANCEL'
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//BRE Hub//Volunteer shift//EN',
    'CALSCALE:GREGORIAN',
    `METHOD:${input.method}`,
    ...CHICAGO_TIMEZONE,
    'BEGIN:VEVENT',
    `UID:${volunteerShiftUid(f.assignmentId, input.hubOrigin)}`,
    `SEQUENCE:${Math.max(0, Math.trunc(f.sequence))}`,
    `DTSTAMP:${dtstamp}`,
    `DTSTART;TZID=America/Chicago:${local(f.startsLocal)}`,
    `DTEND;TZID=America/Chicago:${local(f.endsLocal)}`,
    `SUMMARY:${escapeText(f.title)}`,
    `ORGANIZER;CN=${escapeParam(input.organizer.name)}:mailto:${input.organizer.email}`,
  ]
  if (cancel && input.attendeeEmail) lines.push(`ATTENDEE:mailto:${input.attendeeEmail}`)
  if (f.location) lines.push(`LOCATION:${escapeText(f.location)}`)
  if (f.link) lines.push(`DESCRIPTION:${escapeText(f.link)}`, `URL:${f.link}`)
  lines.push(`STATUS:${cancel ? 'CANCELLED' : 'CONFIRMED'}`, 'TRANSP:OPAQUE', 'END:VEVENT', 'END:VCALENDAR')
  return lines.map(fold).join('\r\n') + '\r\n'
}

// A parameter value (CN) is quoted when it holds a colon, semicolon or comma;
// a double quote can't appear in one at all.
const escapeParam = (value: string) => {
  const clean = value.replace(/["\r\n]/g, '')
  return /[:;,]/.test(clean) ? `"${clean}"` : clean
}

// ------------------------------------------------------ RSVP links (ADR 0072)

// The three buttons in a meeting notice email, each a personal signed link
// on the forms host: https://forms.bre.live/r/<token>/<choice>. The token
// names one meeting and one person and when it stops working (a day after
// the meeting starts); it is an HMAC under a key derived from
// HUB_CORE_ACTOR_SECRET, so nothing is stored before it is used and an
// edited token reaches no other meeting or person.
//
//   r1.<meetingId>.<personId>.<expiresAt unix seconds>.<signature>

export const MEETING_RSVP_CHOICES = ['yes', 'no', 'maybe'] as const
export type MeetingRsvpChoice = (typeof MEETING_RSVP_CHOICES)[number]
export const MEETING_RSVP_CHOICE_LABELS: Record<MeetingRsvpChoice, string> = { yes: 'Attending', no: 'Not attending', maybe: 'Maybe' }
// The RSVP link's own actor toward Platform Core: no person, and it reaches
// /v1/public/meeting-rsvp/* only.
export const MEETING_RSVP_PUBLIC_SUB = 'system:meeting-rsvp'
// How long after the meeting starts a link still opens (to say it's closed).
export const MEETING_RSVP_TOKEN_GRACE_MS = 86_400_000

const RSVP_TOKEN = /^r1\.([A-Za-z0-9_-]{3,80})\.([A-Za-z0-9_-]{3,80})\.(\d{9,11})\.([A-Za-z0-9_-]{32})$/
const rsvpEncoder = new TextEncoder()

async function rsvpMac(secret: string, body: string): Promise<string> {
  const key = await crypto.subtle.importKey('raw', rsvpEncoder.encode(`hub-meeting-rsvp-v1:${secret}`), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign'])
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, rsvpEncoder.encode(body)))
  let binary = ''
  for (const byte of mac) binary += String.fromCharCode(byte)
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '').slice(0, 32)
}

export type MeetingRsvpToken = { meetingId: string; personId: string; expiresAt: number }

export async function signMeetingRsvpToken(secret: string, token: MeetingRsvpToken): Promise<string> {
  if (!secret || secret.length < 32) throw new Error('The token secret must be at least 32 characters')
  const body = `r1.${token.meetingId}.${token.personId}.${token.expiresAt}`
  if (!RSVP_TOKEN.test(`${body}.${'x'.repeat(32)}`)) throw new Error('Not a meeting or person id a token can carry')
  return `${body}.${await rsvpMac(secret, body)}`
}

export type MeetingRsvpTokenCheck = { ok: true; token: MeetingRsvpToken } | { ok: false; reason: 'malformed' | 'bad_signature' | 'expired' }

export async function verifyMeetingRsvpToken(secret: string, raw: string, nowMs = Date.now()): Promise<MeetingRsvpTokenCheck> {
  const match = RSVP_TOKEN.exec(raw ?? '')
  if (!match) return { ok: false, reason: 'malformed' }
  const [, meetingId, personId, exp, signature] = match
  const expected = await rsvpMac(secret, raw.slice(0, raw.length - 33))
  let diff = expected.length === signature!.length ? 0 : 1
  for (let i = 0; i < expected.length && i < signature!.length; i += 1) diff |= expected.charCodeAt(i) ^ signature!.charCodeAt(i)
  if (diff !== 0) return { ok: false, reason: 'bad_signature' }
  const expiresAt = Number(exp)
  if (expiresAt * 1000 <= nowMs) return { ok: false, reason: 'expired' }
  return { ok: true, token: { meetingId: meetingId!, personId: personId!, expiresAt } }
}

// What the confirm page shows (core's read; it changes nothing).
export type MeetingRsvpView = {
  state: 'open' | 'closed' | 'canceled'
  title: string
  startsLocal: string
  location: string | null
  // Their answer now, as one of the three choices; null if none.
  current: MeetingRsvpChoice | null
  // Not attending takes a reason only where it becomes an excused-absence
  // request (board meetings).
  reasonAsks: boolean
}

export type MeetingRsvpAnswer = { choice: MeetingRsvpChoice; excused: boolean }
